import { ConfirmationModal } from "@repo/ui/Modal/confirmationModal";
import { useCallback, useRef, type ReactNode } from "react";
import { useBeforeUnload, useBlocker } from "react-router";

interface UnsavedChangesGuard {
  guardModal: ReactNode;
  navigateWithoutConfirmation: (navigate: () => void) => void;
}

/** Blocks in-app and browser navigation while an explicit-save form is dirty. */
export function useUnsavedChangesGuard(
  hasUnsavedChanges: boolean,
): UnsavedChangesGuard {
  const bypassNextNavigationRef = useRef(false);
  const navigationBlocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (bypassNextNavigationRef.current) {
      bypassNextNavigationRef.current = false;
      return false;
    }
    return (
      hasUnsavedChanges && currentLocation.pathname !== nextLocation.pathname
    );
  });

  useBeforeUnload(
    useCallback(
      (event) => {
        if (hasUnsavedChanges) {
          event.preventDefault();
          event.returnValue = "";
        }
      },
      [hasUnsavedChanges],
    ),
  );

  const navigateWithoutConfirmation = useCallback((navigate: () => void) => {
    bypassNextNavigationRef.current = true;
    navigate();
  }, []);

  return {
    navigateWithoutConfirmation,
    guardModal: (
      <ConfirmationModal
        isOpen={navigationBlocker.state === "blocked"}
        onOpenChange={(isOpen) => {
          if (!isOpen && navigationBlocker.state === "blocked") {
            navigationBlocker.reset();
          }
        }}
        title="Discard unsaved changes?"
        confirmText="Discard"
        onConfirm={() => {
          if (navigationBlocker.state === "blocked") {
            navigationBlocker.proceed();
          }
        }}
      >
        Your changes haven&apos;t been saved. Discard them and leave this page?
      </ConfirmationModal>
    ),
  };
}
